/** Which layout the viewport earns.
 *
 *  `--bujo-bp-two-day: 720px` and `--bujo-bp-two-lane: 1024px` are the tokens;
 *  the rule is "width buys more journal, never denser journal".
 *
 *  **Orientation is part of it.** The spec's two tables disagree if you read
 *  width alone: the breakpoint table puts 720–1023 at *two days, one lane each*,
 *  while the iPad table puts an 11" portrait — 834px — at *one day, two lanes*.
 *  Orientation reconciles them, and it is the reading that makes sense: a tall
 *  narrow-ish viewport suits one day with two lanes, a short wide one suits two
 *  days side by side. Height is therefore load-bearing in this decision, not
 *  incidental.
 */

export const BP_TWO_DAY = 720;
export const BP_TWO_LANE = 1024;

export interface Layout {
  /** How many days are shown side by side. Two is the ceiling, always. */
  days: 1 | 2;
  /** Lanes *inside* one day: schedule and rapid log split, or stacked. */
  lanes: 1 | 2;
  /** The closing ritual: inside the column, or a band under the day.
   *
   *  A band only where one day is shown in two lanes. On a two-day spread each
   *  page is a day and closes itself — a single band under both pages belonged
   *  to the right-hand day alone, which read as if it covered the pair.
   *
   *  This departs from the handoff on purpose: `design/handoff/specs/
   *  responsive-spec.md` draws one band across the spread. In use, each page
   *  of a book closes its own day, and that is what the owner asked for. */
  ritual: 'in-column' | 'band';
  /** Section navigation: the bottom bar, or a left rail with no icons. */
  nav: 'bottom' | 'rail';
  /** `TWO-DAY SPREAD`, which the phone layout does not badge because it is the
   *  only mode there. */
  badge: string | null;
}

export function layoutFor(width: number, height: number): Layout {
  if (width < BP_TWO_DAY) {
    // The responsive spec calls `◆ FOCUSED` "implicit and unbadged" at this
    // width, but every 390px frame draws it and the design brief says the
    // one-day focus "is marked `◆ FOCUSED` in the header". The frames win on
    // presentation — and dropping a badge that has been on screen since the
    // first build would read as a regression, not as a spec being honoured.
    return { days: 1, lanes: 1, ritual: 'in-column', nav: 'bottom', badge: 'FOCUSED' };
  }
  if (width < BP_TWO_LANE) {
    return width > height
      ? { days: 2, lanes: 1, ritual: 'in-column', nav: 'bottom', badge: 'TWO-DAY SPREAD' }
      : { days: 1, lanes: 2, ritual: 'band', nav: 'bottom', badge: 'TWO LANES' };
  }
  return { days: 2, lanes: 2, ritual: 'in-column', nav: 'rail', badge: 'TWO-DAY SPREAD' };
}

/** Width available to one day's column, after the rail, the gutters and the
 *  gap between pages. Needed by the measured fallback, which has to know what
 *  it is measuring against. */
export function columnWidth(width: number, layout: Layout, gutter = 20, gap = 20): number {
  const rail = layout.nav === 'rail' ? RAIL_WIDTH : 0;
  const usable = width - rail - gutter * 2 - (layout.days - 1) * gap;
  return Math.floor(usable / layout.days);
}

/** The rail's width.
 *
 *  Wide enough for the longest thing it lists. The frame's rail carries filter
 *  tokens beside their open counts, and `#KovackLaunch` at `--bujo-fs-meta` on
 *  the mono grid is 94px before the two gutters and the count — so the 132px a
 *  bare tab bar needed would have clipped the row that makes the section useful.
 */
export const RAIL_WIDTH = 176;

/** Where the closing ritual goes for a given day and lane count — see
 *  `Layout.ritual`. Anything that changes `days` or `lanes` after the fact
 *  (R12's fallback, the one-day toggle) re-derives it here. */
export function ritualFor(days: 1 | 2, lanes: 1 | 2): Layout['ritual'] {
  return days === 1 && lanes === 2 ? 'band' : 'in-column';
}
